import { useTranslation } from 'react-i18next';
import type { Editor } from '@tiptap/react';
import type { BriefSectionResponseDto } from '@inference.engine/workspace-api-client/web';
import { formatSectionValue, parseSectionValue, tiptapToMarkdown } from '@/entities/brief';
import type { BriefCommentAnnotation } from '@/features/comment-on-file';
import { BriefSectionEditor } from '../BriefSectionEditor';
import {
  BriefSectionShell,
  type AiSuggestionActions,
  type BriefSectionHelperTextKey,
} from '../BriefSectionShell';
import { DateField, type DateFieldValue } from './widgets/DateField';
import { SelectField, type SelectFieldValue } from './widgets/SelectField';
import { ChecklistField, type ChecklistFieldValue } from './widgets/ChecklistField';
import { UrlField, type UrlFieldValue } from './widgets/UrlField';
import { type FileFieldValue } from './widgets/FileField';
import { FileFieldContainer } from './widgets/FileFieldContainer';
import { BooleanField, type BooleanFieldValue } from './widgets/BooleanField';
import { AgentProseNote } from './AgentProseNote';

interface BriefSectionFieldProps {
  section: BriefSectionResponseDto;
  onContentChange: (sectionId: string, content: unknown) => void;
  onValueChange: (sectionId: string, value: unknown) => void;
  onEditorFocus?: (editor: Editor) => void;
  registerEditor?: (sectionId: string, editor: Editor | null) => void;
  onAddCommentInitiated?: () => void;
  onHighlightClick?: () => void;
  annotations?: BriefCommentAnnotation[];
  /**
   * Fires when the shell's "Comment on section" affordance is clicked, for any section — the
   * caller seeds `{ kind: 'field', sectionId }` as the pending annotation for the next comment.
   * Prose sections get it too now that they carry the same chrome: it anchors a comment to the
   * whole section, alongside (not instead of) the text-selection flow that still anchors a
   * comment to a range within them.
   */
  onCommentOnField?: (sectionId: string) => void;
  /** Set while this section holds a value an agent wrote that the briefer has not confirmed. */
  aiSuggestion?: AiSuggestionActions;
  /** The page's pinned last-focused editor, so a prose section can tell if the bar is its own. */
  activeEditor?: Editor | null;
  /** Comments anchored to this section, of either kind. */
  commentCount?: number;
  /** Opens this section's thread in the rail. */
  onOpenComments?: () => void;
  /** Set when a "Mark as ready" attempt found this required section empty. */
  missingRequired?: boolean;
}

const HELPER_TEXT_KEY: Record<string, BriefSectionHelperTextKey> = {
  text: 'projectBriefDetail.briefSectionShell.text.helperText',
  date: 'projectBriefDetail.briefSectionShell.date.helperText',
  select: 'projectBriefDetail.briefSectionShell.select.helperText',
  checklist: 'projectBriefDetail.briefSectionShell.checklist.helperText',
  url: 'projectBriefDetail.briefSectionShell.url.helperText',
  file: 'projectBriefDetail.briefSectionShell.file.helperText',
  boolean: 'projectBriefDetail.briefSectionShell.boolean.helperText',
};

const DEFAULT_HELPER_TEXT_KEY: BriefSectionHelperTextKey =
  'projectBriefDetail.briefSectionShell.text.helperText';

/**
 * Whether the widget already holds an answer. Reuses the export formatter instead of a second
 * emptiness table: it renders '' for exactly the values that mean "nothing entered" (no date
 * endpoint, nothing selected, no url, no file, no boolean answer) and a non-empty string for
 * every answered one - `false` included, which renders as its configured label.
 */
function hasWidgetValue(section: BriefSectionResponseDto): boolean {
  return formatSectionValue(section.type ?? null, section.config, section.value) !== '';
}

/**
 * Whether the section's TipTap `content` holds visible text. The markdown serializer already
 * answers this and trims, so an empty doc and a doc of empty paragraphs both come back ''.
 */
function hasProseContent(content: unknown): boolean {
  return tiptapToMarkdown(content as Record<string, unknown> | null) !== '';
}

/**
 * Dispatches a brief section to its control by `section.type`, then wraps EVERY section in
 * `BriefSectionShell` so the brief reads as one redesigned document rather than a redesigned
 * form bolted onto old prose:
 *  - `type == null` and any type this client does not recognise → `BriefSectionEditor`
 *  - `'text'` → `BriefSectionEditor`
 *  - the six other types → their widget
 *
 * An untyped section keeps its behaviour — edited as TipTap prose, saved through
 * `onContentChange`, unchanged on the wire. Only its chrome is new. Reading the spec's
 * backward-compatibility line as "prose keeps the old look" was the mistake this routing
 * corrects: the prototype redesigns the whole document, prose sections included.
 *
 * Every typed value is parsed through the shared Zod boundary before a widget sees it — a
 * malformed value (a race, a bad migration, manual DB edits) degrades to "no value" rather
 * than crashing the section.
 */
export function BriefSectionField({
  section,
  onContentChange,
  onValueChange,
  onEditorFocus,
  registerEditor,
  onAddCommentInitiated,
  onHighlightClick,
  annotations,
  onCommentOnField,
  aiSuggestion,
  activeEditor,
  commentCount,
  onOpenComments,
  missingRequired,
}: BriefSectionFieldProps) {
  const { t } = useTranslation('projects');

  const parsed =
    section.type == null ? null : parseSectionValue(section.type, section.value, section.config);
  const value = parsed?.success ? parsed.data : null;
  const handleChange = (next: unknown) => onValueChange(section.id, next);
  const helperTextKey =
    (section.type == null ? undefined : HELPER_TEXT_KEY[section.type]) ?? DEFAULT_HELPER_TEXT_KEY;

  const editorProps = {
    section,
    onChange: onContentChange,
    onEditorFocus,
    registerEditor,
    onAddCommentInitiated,
    onHighlightClick,
    annotations,
    activeEditor,
    // An empty section prompts with what belongs in it: the section's own description when the
    // template carries one, otherwise the type's description, the same text the ⓘ tooltip shows.
    placeholder: section.aiInstructions?.trim() || t(helperTextKey),
  };

  let control;
  // Set by the two branches that put `section.content` on screen themselves. Only the widget
  // branches leave it unrendered, and only they can strand prose.
  let rendersProse = false;
  switch (section.type) {
    case null:
    case undefined:
    case 'text':
      control = <BriefSectionEditor {...editorProps} />;
      rendersProse = true;
      break;
    case 'date':
      control = (
        <DateField
          value={value as DateFieldValue | null}
          config={section.config}
          onChange={handleChange}
        />
      );
      break;
    case 'select':
      control = (
        <SelectField
          value={value as SelectFieldValue | null}
          config={section.config}
          onChange={handleChange}
        />
      );
      break;
    case 'checklist':
      control = (
        <ChecklistField
          value={value as ChecklistFieldValue | null}
          config={section.config}
          onChange={handleChange}
        />
      );
      break;
    case 'url':
      control = (
        <UrlField
          value={value as UrlFieldValue | null}
          config={section.config}
          onChange={handleChange}
        />
      );
      break;
    case 'file':
      control = (
        <FileFieldContainer
          value={value as FileFieldValue | null}
          config={section.config}
          onChange={handleChange}
        />
      );
      break;
    case 'boolean':
      control = (
        <BooleanField
          value={value as BooleanFieldValue | null}
          config={section.config}
          onChange={handleChange}
        />
      );
      break;
    default:
      // Unrecognized type from a future rollout this client doesn't know yet — degrade to
      // the prose editor rather than rendering nothing. It still gets the shell, so an
      // unknown section reads as part of the document instead of falling out of it.
      control = <BriefSectionEditor {...editorProps} />;
      rendersProse = true;
      break;
  }

  // A brief an agent generated from a typed template arrives with the model's prose in
  // `content` and no `value`: generation writes content only, and brief creation stamps the
  // template's type on afterwards. The widget shows none of it, so the note carries it into
  // view. It is gone the moment the field has an answer - see `AgentProseNote` for why the two
  // must never be on screen together.
  const showAgentProse =
    !rendersProse && !hasWidgetValue(section) && hasProseContent(section.content);

  const handleShellComment = () => {
    onCommentOnField?.(section.id);
    onAddCommentInitiated?.();
  };

  return (
    <BriefSectionShell
      section={section}
      sectionId={section.id}
      helperTextKey={helperTextKey}
      onAddCommentInitiated={handleShellComment}
      aiSuggestion={aiSuggestion}
      commentCount={commentCount}
      onOpenComments={onOpenComments}
      belowControl={showAgentProse ? <AgentProseNote content={section.content} /> : undefined}
      missingRequired={missingRequired}
    >
      {control}
    </BriefSectionShell>
  );
}
